import { ReactNode } from 'react'
import { formatAddress, statusLabelPdf, stripSogcMarkup } from '../../helpers/zefix.helper'
import { ZefixCompanyShortPdf, ZefixPdfContentProps } from '../../types/zefix.type'

export function CompanyShortList({ items }: { readonly items: ZefixCompanyShortPdf[] }) {
  return (
    <div className='d-flex flex-column gap-1'>
      {items.map((c, i) => (
        <div key={i} className='zefix-pdf__company-item'>
          {[c.name, c.legalForm?.name?.de ?? c.legalForm?.shortName?.de, c.legalSeat, c.uid]
            .filter(Boolean)
            .join(' · ')}
        </div>
      ))}
    </div>
  )
}

export function PdfSection({
  title,
  children,
}: {
  readonly title?: string
  readonly children: ReactNode
}) {
  return (
    <div className='zefix-pdf__section'>
      {title && <div className='zefix-pdf__section-title'>{title}</div>}
      {children}
    </div>
  )
}

export function PdfRow({ label, value }: { readonly label: string; readonly value: ReactNode }) {
  if (!value) return null
  return (
    <div className='zefix-pdf__row'>
      <div className='zefix-pdf__row-label'>{label}</div>
      <div className='zefix-pdf__row-value'>{value}</div>
    </div>
  )
}

export function ZefixPdfContent({ data, pdfRef }: ZefixPdfContentProps) {
  const today = new Date().toLocaleDateString('de-CH', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
  const address = formatAddress(data.address)

  return (
    <div ref={pdfRef} className='zefix-pdf'>
      {/* Header */}
      <div className='zefix-pdf__header'>
        <div className='zefix-pdf__header-sub'>Schweizerische Eidgenossenschaft</div>
        <div className='zefix-pdf__header-title'>Commercial register of the canton of Schwyz</div>
        <div className='zefix-pdf__header-date'>Auszug vom {today}</div>
      </div>

      {/* Company identity */}
      <PdfSection title='Firma'>
        <div className='zefix-pdf__company-name'>{data.name ?? '-'}</div>
        <PdfRow label='UID' value={data.uid} />
        <PdfRow label='CHID' value={data.chid} />
        <PdfRow label='EHRA-ID' value={data.ehraid} />
        <PdfRow
          label='Handelsregister-Nr.'
          value={data.registryOfCommerceId ? `Nr. ${data.registryOfCommerceId}` : null}
        />
        {data.translation && data.translation.length > 0 && (
          <PdfRow label='Übersetzung' value={data.translation.join(' / ')} />
        )}
        {data.oldNames && data.oldNames.length > 0 && (
          <PdfRow
            label='Frühere Firmen'
            value={data.oldNames
              .map(o => o.name)
              .filter(Boolean)
              .join(', ')}
          />
        )}
      </PdfSection>

      {/* Legal details */}
      <PdfSection title='Rechtsform & Status'>
        <PdfRow
          label='Rechtsform'
          value={data.legalForm?.name?.de ?? data.legalForm?.shortName?.de}
        />
        <PdfRow label='Sitz' value={data.legalSeat} />
        <PdfRow label='Kanton' value={data.canton} />
        <PdfRow label='Status' value={statusLabelPdf(data.status)} />
      </PdfSection>

      {/* Address */}
      {address && (
        <PdfSection title='Adresse'>
          <PdfRow label='Domizil' value={address} />
        </PdfSection>
      )}

      {/* Capital */}
      {(data.capitalNominal || data.capitalCurrency) && (
        <PdfSection title='Kapital'>
          <PdfRow
            label='Kapital'
            value={[data.capitalCurrency, data.capitalNominal].filter(Boolean).join(' ')}
          />
        </PdfSection>
      )}

      {/* Purpose */}
      {data.purpose && (
        <PdfSection title='Zweck'>
          <p className='zefix-pdf__purpose'>{data.purpose}</p>
        </PdfSection>
      )}

      {/* Offices & relations */}
      {((data.headOffices?.length ?? 0) > 0 ||
        (data.furtherHeadOffices?.length ?? 0) > 0 ||
        (data.branchOffices?.length ?? 0) > 0 ||
        (data.hasTakenOver?.length ?? 0) > 0 ||
        (data.wasTakenOverBy?.length ?? 0) > 0 ||
        (data.auditCompanies?.length ?? 0) > 0) && (
        <PdfSection title='Verbundene Gesellschaften'>
          {data.headOffices && data.headOffices.length > 0 && (
            <PdfRow
              label='Hauptniederlassung'
              value={<CompanyShortList items={data.headOffices} />}
            />
          )}
          {data.furtherHeadOffices && data.furtherHeadOffices.length > 0 && (
            <PdfRow
              label='Weitere Niederlassungen'
              value={<CompanyShortList items={data.furtherHeadOffices} />}
            />
          )}
          {data.branchOffices && data.branchOffices.length > 0 && (
            <PdfRow
              label='Zweigniederlassungen'
              value={<CompanyShortList items={data.branchOffices} />}
            />
          )}
          {data.hasTakenOver && data.hasTakenOver.length > 0 && (
            <PdfRow label='Übernommen' value={<CompanyShortList items={data.hasTakenOver} />} />
          )}
          {data.wasTakenOverBy && data.wasTakenOverBy.length > 0 && (
            <PdfRow
              label='Übernommen von'
              value={<CompanyShortList items={data.wasTakenOverBy} />}
            />
          )}
          {data.auditCompanies && data.auditCompanies.length > 0 && (
            <PdfRow
              label='Revisionsstelle'
              value={<CompanyShortList items={data.auditCompanies} />}
            />
          )}
        </PdfSection>
      )}

      {/* SOGC Publications */}
      {data.sogcPub && data.sogcPub.length > 0 && (
        <PdfSection title='SHAB-Publikationen'>
          {data.sogcPub.map((pub, i) => (
            <div key={i} className='zefix-pdf__pub-item'>
              {pub.sogcDate && (
                <div className='zefix-pdf__pub-meta'>
                  {pub.sogcDate}
                  {pub.registryOfCommerceCanton ? ` · ${pub.registryOfCommerceCanton}` : ''}
                  {pub.registryOfCommerceJournalDate
                    ? ` · ${pub.registryOfCommerceJournalDate}`
                    : ''}
                  {pub.mutationTypes &&
                    pub.mutationTypes.length > 0 &&
                    ` · ${pub.mutationTypes
                      .map(m => m.key)
                      .filter(Boolean)
                      .join(', ')}`}
                </div>
              )}
              {pub.message && (
                <div className='zefix-pdf__pub-message'>{stripSogcMarkup(pub.message)}</div>
              )}
            </div>
          ))}
        </PdfSection>
      )}

      {/* Links */}
      {(data.zefixDetailWeb?.de || data.cantonalExcerptWeb) && (
        <PdfSection title='Online-Verweise'>
          {data.zefixDetailWeb?.de && <PdfRow label='Zefix' value={data.zefixDetailWeb.de} />}
          {data.cantonalExcerptWeb && (
            <PdfRow label='Kantonaler Auszug' value={data.cantonalExcerptWeb} />
          )}
        </PdfSection>
      )}

      {/* Footer */}
      <div className='zefix-pdf__footer'>
        <p className='zefix-pdf__footer-text'>
          Handelsregisteramt des Kantons Schwyz · Dieser Auszug hat keine rechtliche Gültigkeit ohne
          amtliche Beglaubigung.
        </p>
      </div>
    </div>
  )
}
